type GSplatShaderDefines = Record<string, string | number | boolean>

const HOOK_DEFINES = [
  ['modifySplatCenter', 'CUSTOM_MODIFY_SPLAT_CENTER'],
  ['modifySplatScale', 'CUSTOM_MODIFY_SPLAT_SCALE'],
  ['modifySplatCovariance', 'CUSTOM_MODIFY_SPLAT_COVARIANCE'],
  ['modifySplatColor', 'CUSTOM_MODIFY_SPLAT_COLOR'],
  ['modifyCenter', 'CUSTOM_MODIFY_CENTER'],
  ['modifyCovariance', 'CUSTOM_MODIFY_COVARIANCE'],
  ['modifyColor', 'CUSTOM_MODIFY_COLOR'],
] as const

const PLAYCANVAS_ROTATION_SCALE_HOOK = 'modifySplatRotationScale'
const GSPLAT_FRAGMENT_HOOK = 'modifySplatFragment'

export const DEFAULT_GSPLAT_MODIFY_CHUNK = /* glsl */ `
// ============================================================
// GSplat Modify - PlayCanvas 风格自定义钩子函数
// 对标 PlayCanvas gsplatModifyVS，保留旧 gsplatCustomizeVS 作为内部兼容入口。
// ============================================================

// 外部特效优先实现 modifySplat* 函数。旧 modify* 函数作为兼容别名保留。

#ifndef CUSTOM_MODIFY_SPLAT_CENTER
// 修改中心位置钩子
// 用法示例：center.y += 1.0; // 上移 1 单位
void modifySplatCenter(inout vec3 center) {
  #ifdef CUSTOM_MODIFY_CENTER
    modifyCenter(center);
  #endif
  // 默认不修改
}
#endif

#ifndef CUSTOM_MODIFY_SPLAT_SCALE
// 修改构建 covariance 前的原始三轴缩放。
// 用法示例：
//   float shortest = min(scale.x, min(scale.y, scale.z));
//   scale = vec3(shortest);  // 以最短主轴为半径圆化 splat
void modifySplatScale(inout vec3 scale) {
  // 默认不修改
}
#endif

#ifndef CUSTOM_MODIFY_SPLAT_COVARIANCE
// 修改协方差钩子，对应 PlayCanvas modifySplatRotationScale 的 Share3DEngine 版本。
// 参数：originalCenter - 原始中心位置
//       modifiedCenter - 经过 modifySplatCenter 修改后的中心位置
//       covA, covB - 协方差矩阵上三角元素
// 用法示例：
//   gsplatApplyUniformScale(covA, covB, 2.0);  // 半径放大 2 倍，covariance 内部按 4 倍缩放
//   float size = gsplatExtractSize(covA, covB);
//   gsplatMakeRound(covA, covB, size * 0.5);   // 变成圆形 splat
//   gsplatMakeRound(covA, covB, 0.0);          // 隐藏 splat
void modifySplatCovariance(vec3 originalCenter, vec3 modifiedCenter, inout vec3 covA, inout vec3 covB) {
  #ifdef CUSTOM_MODIFY_COVARIANCE
    modifyCovariance(originalCenter, modifiedCenter, covA, covB);
  #endif
  // 默认不修改
}
#endif

#ifndef CUSTOM_MODIFY_SPLAT_COLOR
// 修改颜色钩子
// 参数：center - 当前 splat 中心位置
//       color - RGBA 颜色（xyz=RGB，w=透明度）
// 用法示例：color.rgb *= 0.5; // 变暗
void modifySplatColor(vec3 center, inout vec4 color) {
  #ifdef CUSTOM_MODIFY_COLOR
    modifyColor(center, color);
  #endif
  // 默认不修改
}
#endif

#ifndef CUSTOM_MODIFY_CENTER
void modifyCenter(inout vec3 center) {
  modifySplatCenter(center);
}
#endif

#ifndef CUSTOM_MODIFY_COVARIANCE
void modifyCovariance(vec3 originalCenter, vec3 modifiedCenter, inout vec3 covA, inout vec3 covB) {
  modifySplatCovariance(originalCenter, modifiedCenter, covA, covB);
}
#endif

#ifndef CUSTOM_MODIFY_COLOR
void modifyColor(vec3 center, inout vec4 color) {
  modifySplatColor(center, color);
}
#endif
`

export const DEFAULT_GSPLAT_MODIFY_FRAGMENT_CHUNK = /* glsl */ `
// ============================================================
// GSplat Fragment Modify - fragment hook
// gaussianFootprint 为局部高斯坐标；premultipliedColor/alpha 是最终片元输入。
// ============================================================
void modifySplatFragment(
  in vec2 gaussianFootprint,
  in vec4 sourceColor,
  inout vec3 premultipliedColor,
  inout float alpha
) {}
`

const DEFAULT_GSPLAT_MODIFY_WGSL_HOOKS = /* wgsl */ `
fn modifySplatCenter(center: ptr<function, vec3f>) {}
fn modifySplatRotationScale(
  originalCenter: vec3f,
  modifiedCenter: vec3f,
  rotation: ptr<function, vec4f>,
  scale: ptr<function, vec3f>
) {}
fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {}
`

const DEFAULT_GSPLAT_MODIFY_FRAGMENT_WGSL = /* wgsl */ `
fn modifySplatFragment(gaussianFootprint: vec2f, color: ptr<function, vec4f>) {}
`

function definesWgslFunction(source: string, name: string): boolean {
  return new RegExp(`\\bfn\\s+${name}\\s*\\(`).test(source)
}

function extractWgslFunction(source: string, name: string): string | null {
  const match = new RegExp(`\\bfn\\s+${name}\\s*\\(`).exec(source)
  if (!match) return null

  const bodyStart = source.indexOf('{', match.index)
  if (bodyStart < 0) return null

  let depth = 0
  let inLineComment = false
  let inBlockComment = false
  for (let index = bodyStart; index < source.length; index++) {
    const char = source[index]
    const next = source[index + 1]

    if (inLineComment) {
      if (char === '\n' || char === '\r') inLineComment = false
      continue
    }
    if (inBlockComment) {
      if (char === '*' && next === '/') {
        inBlockComment = false
        index++
      }
      continue
    }
    if (char === '/' && next === '/') {
      inLineComment = true
      index++
      continue
    }
    if (char === '/' && next === '*') {
      inBlockComment = true
      index++
      continue
    }
    if (char === '{') depth++
    if (char === '}' && --depth === 0) return source.slice(match.index, index + 1)
  }

  return null
}

/**
 * 为 WebGPU effect/modifier 补齐缺失的 no-op hook。
 *
 * WGSL 没有 GLSL 的预处理器宏，因此不能直接复用 `createGSplatModifyChunk`。
 * 只追加缺失函数，保留调用方现有 WGSL、uniform 和 texture binding，不触碰 WebGL GLSL。
 */
export function createGSplatModifyWGSLChunk(
  customCode?: string | null,
  defines?: GSplatShaderDefines
): string {
  const source = preprocessGSplatWgslConditionals(
    stripGSplatWgslBindingDeclarations(customCode?.trim() ?? ''),
    defines
  )
  if (!source) return DEFAULT_GSPLAT_MODIFY_WGSL_HOOKS

  const missing = [
    !definesWgslFunction(source, 'modifySplatCenter')
      ? extractWgslFunction(DEFAULT_GSPLAT_MODIFY_WGSL_HOOKS, 'modifySplatCenter')
      : null,
    !definesWgslFunction(source, 'modifySplatRotationScale')
      ? extractWgslFunction(DEFAULT_GSPLAT_MODIFY_WGSL_HOOKS, 'modifySplatRotationScale')
      : null,
    !definesWgslFunction(source, 'modifySplatColor')
      ? extractWgslFunction(DEFAULT_GSPLAT_MODIFY_WGSL_HOOKS, 'modifySplatColor')
      : null,
  ].filter((value): value is string => Boolean(value))

  return [source, ...missing].join('\n')
}

/** 为 WebGPU fragment effect 补齐可选的 no-op fragment hook。 */
export function createGSplatModifyFragmentWGSLChunk(
  customCode?: string | null,
  defines?: GSplatShaderDefines
): string {
  const source = preprocessGSplatWgslConditionals(
    stripGSplatWgslBindingDeclarations(customCode?.trim() ?? ''),
    defines
  )
  if (!source) return DEFAULT_GSPLAT_MODIFY_FRAGMENT_WGSL
  return definesWgslFunction(source, 'modifySplatFragment')
    ? source
    : `${source}\n${DEFAULT_GSPLAT_MODIFY_FRAGMENT_WGSL}`
}

/**
 * WGSL 本身没有 GLSL 预处理器。内置 effect 仍允许用少量 `#ifdef` / `#ifndef` 包住
 * 可选代码，因此在交给 TSL/WGSL 编译器前按 material defines 做一次最小条件展开。
 *
 * 未传 defines 时按“未定义”处理：`#ifdef NAME` 被移除，`#ifndef NAME` 保留。
 * 非条件预处理指令不应出现在 WGSL effect 中，保留原文会让错误直接落到 GPU 编译阶段，
 * 因此这里仅处理已支持的条件指令，不改动普通 shader 内容。
 */
export function preprocessGSplatWgslConditionals(
  source: string,
  defines: GSplatShaderDefines = {}
): string {
  if (!source.includes('#')) return source

  const enabled = (name: string): boolean => {
    const value = defines[name]
    return value !== undefined && value !== false && value !== 0 && value !== ''
  }
  const active: boolean[] = [true]
  const branchTaken: boolean[] = []
  const output: string[] = []

  for (const line of source.split(/\r?\n/)) {
    const directive = /^\s*#\s*(ifdef|ifndef|else|endif)\b(?:\s+([A-Za-z_][A-Za-z0-9_]*))?/.exec(
      line
    )
    if (!directive) {
      if (active[active.length - 1]) output.push(line)
      continue
    }

    const kind = directive[1]
    if (kind === 'ifdef' || kind === 'ifndef') {
      const condition = kind === 'ifdef' ? enabled(directive[2]!) : !enabled(directive[2]!)
      branchTaken.push(condition)
      active.push(active[active.length - 1] && condition)
      continue
    }
    if (kind === 'else') {
      if (branchTaken.length === 0) {
        throw new Error('WGSL effect conditional #else 没有对应的 #if')
      }
      const parent = active[active.length - 2]
      const previous = branchTaken[branchTaken.length - 1]
      branchTaken[branchTaken.length - 1] = !previous
      active[active.length - 1] = parent && !previous
      continue
    }
    if (active.length <= 1 || branchTaken.length === 0) {
      throw new Error('WGSL effect conditional #endif 没有对应的 #if')
    }
    active.pop()
    branchTaken.pop()
  }

  if (active.length !== 1 || branchTaken.length !== 0) {
    throw new Error('WGSL effect conditional 缺少 #endif')
  }
  return output.join('\n')
}

/**
 * Remove binding declarations from custom WGSL before embedding it into a TSL function.
 *
 * Three owns the actual uniform/texture bindings and exposes them through the generated
 * `object` struct. Keeping PlayCanvas-style declarations in the raw function source makes
 * WGSL parser them as statements or duplicate module variables.
 */
export function stripGSplatWgslBindingDeclarations(source: string): string {
  return source
    .replace(/^\s*uniform\s+[A-Za-z_][A-Za-z0-9_]*\s*:\s*[^;]+;\s*$/gm, '')
    .replace(/^\s*var\s+[A-Za-z_][A-Za-z0-9_]*\s*:\s*texture_2d<[^>]+>;\s*$/gm, '')
}

/**
 * Three TSL represents boolean object uniforms as `u32` members and converts them to a
 * temporary boolean only when the uniform node itself is emitted. Custom WGSL is emitted as
 * raw code, so a direct `object.someBool` access would otherwise be used as an integer condition.
 * Normalize boolean modifier accesses before replacing the `uniform.` namespace with `object.`.
 */
export function normalizeGSplatWgslBooleanUniforms(
  source: string,
  parameters: ReadonlyMap<string, unknown>
): string {
  let normalized = source
  for (const [name, value] of parameters) {
    if (typeof value !== 'boolean') continue
    const escapedName = name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    normalized = normalized.replace(
      new RegExp(`\\buniform\\.${escapedName}\\b`, 'g'),
      `(uniform.${name} != 0u)`
    )
  }
  return normalized
}

function definesFunction(source: string, name: string): boolean {
  return new RegExp(`\\bvoid\\s+${name}\\s*\\(`).test(source)
}

function extractFunction(source: string, name: string): string | null {
  const match = new RegExp(`\\bvoid\\s+${name}\\s*\\(`).exec(source)
  if (!match) {
    return null
  }

  const start = match.index
  const bodyStart = source.indexOf('{', match.index)
  if (bodyStart < 0) {
    return null
  }

  let depth = 0
  let inLineComment = false
  let inBlockComment = false
  for (let i = bodyStart; i < source.length; i++) {
    const char = source[i]
    const next = source[i + 1]

    if (inLineComment) {
      if (char === '\n' || char === '\r') {
        inLineComment = false
      }
      continue
    }

    if (inBlockComment) {
      if (char === '*' && next === '/') {
        inBlockComment = false
        i++
      }
      continue
    }

    if (char === '/' && next === '/') {
      inLineComment = true
      i++
      continue
    }

    if (char === '/' && next === '*') {
      inBlockComment = true
      i++
      continue
    }

    if (char === '{') {
      depth++
    } else if (char === '}') {
      depth--
      if (depth === 0) {
        return source.slice(start, i + 1)
      }
    }
  }

  return null
}

function assertNoUnsupportedPlayCanvasHooks(source: string): void {
  if (!definesFunction(source, PLAYCANVAS_ROTATION_SCALE_HOOK)) {
    return
  }

  throw new Error(
    '[GSplatModifyChunk] Unsupported PlayCanvas hook "modifySplatRotationScale". ' +
      'Share3DEngine uses covariance hooks; port the shader to modifySplatCovariance(originalCenter, modifiedCenter, covA, covB).'
  )
}

/**
 * 生成带默认兜底的 PlayCanvas 风格 `gsplatModifyVS` chunk。
 *
 * 调用方可以只实现一个或几个 hook，未实现的 hook 会回退到默认 no-op，
 * 避免 shader 固定调用各类 hook 时因为缺函数而编译失败。
 */
export function createGSplatModifyChunk(customCode?: string | null): string {
  const source = customCode?.trim()
  if (!source) {
    return DEFAULT_GSPLAT_MODIFY_CHUNK
  }

  assertNoUnsupportedPlayCanvasHooks(source)

  const defines = HOOK_DEFINES.filter(([functionName]) =>
    definesFunction(source, functionName)
  ).map(([, defineName]) => `#define ${defineName} 1`)

  return `${defines.join('\n')}\n${source}\n${DEFAULT_GSPLAT_MODIFY_CHUNK}`
}

/**
 * 生成仅保留 scale/covariance hook 的 `gsplatModifyVS` chunk。
 *
 * WorkBuffer transform 路径中 center/color modifier 已在 copy pass 执行；
 * final draw 阶段只需要迁移 scale/covariance hook，避免 center/color 被重复应用。
 *
 * 注意：copy pass 与 final draw pass 是两次独立 shader 调用。covariance hook 可以复用
 * uniform / helper / 常量全局，但不能依赖 center/color hook 在运行时写入的全局变量。
 */
export function createGSplatCovarianceOnlyModifyChunk(customCode?: string | null): string {
  const source = customCode?.trim()
  if (!source) {
    return DEFAULT_GSPLAT_MODIFY_CHUNK
  }

  assertNoUnsupportedPlayCanvasHooks(source)

  // Replace center and color hooks with no-ops so they don't run twice,
  // but preserve all other helper code (uniforms, globals, utility functions).
  const centerNoop = 'void modifySplatCenter(inout vec3 center) {}\n'
  const legacyCenterNoop = 'void modifyCenter(inout vec3 center) {}\n'
  const colorNoop = 'void modifySplatColor(vec3 center, inout vec4 color) {}\n'
  const legacyColorNoop = 'void modifyColor(vec3 center, inout vec4 color) {}\n'

  let patched = source

  const centerMatch = extractFunction(patched, 'modifySplatCenter')
  if (centerMatch) {
    patched = patched.replace(centerMatch, centerNoop)
  }

  const legacyCenterMatch = extractFunction(patched, 'modifyCenter')
  if (legacyCenterMatch) {
    patched = patched.replace(legacyCenterMatch, legacyCenterNoop)
  }

  const colorMatch = extractFunction(patched, 'modifySplatColor')
  if (colorMatch) {
    patched = patched.replace(colorMatch, colorNoop)
  }

  const legacyColorMatch = extractFunction(patched, 'modifyColor')
  if (legacyColorMatch) {
    patched = patched.replace(legacyColorMatch, legacyColorNoop)
  }

  const defines = HOOK_DEFINES.filter(([functionName]) =>
    definesFunction(patched, functionName)
  ).map(([, defineName]) => `#define ${defineName} 1`)

  return `${defines.join('\n')}\n${patched}\n${DEFAULT_GSPLAT_MODIFY_CHUNK}`
}

/**
 * 生成带默认 no-op 的 fragment customization chunk。
 *
 * Fragment hook 在 gaussian falloff、alpha cutoff 与预乘之前执行。
 */
export function createGSplatModifyFragmentChunk(customCode?: string | null): string {
  const source = customCode?.trim()
  if (!source) return DEFAULT_GSPLAT_MODIFY_FRAGMENT_CHUNK
  if (definesFunction(source, GSPLAT_FRAGMENT_HOOK)) {
    const hasPremultipliedSignature =
      /void\s+modifySplatFragment\s*\(\s*(?:in\s+)?vec2\s+\w+\s*,\s*(?:in\s+)?vec4\s+\w+\s*,\s*inout\s+vec3\s+\w+\s*,\s*inout\s+float\s+\w+/s.test(
        source
      )
    if (hasPremultipliedSignature) {
      return `#define CUSTOM_MODIFY_SPLAT_FRAGMENT 1
${source}`
    }

    return `#define CUSTOM_MODIFY_SPLAT_FRAGMENT 1
${source}
void modifySplatFragment(
  in vec2 gaussianFootprint,
  in vec4 sourceColor,
  inout vec3 premultipliedColor,
  inout float alpha
) {
  vec4 color = sourceColor;
  modifySplatFragment(gaussianFootprint, color);
  alpha = color.a;
  premultipliedColor = color.rgb * alpha;
}
`
  }
  return `${source}\n${DEFAULT_GSPLAT_MODIFY_FRAGMENT_CHUNK}`
}
